import {
  DropdownGroup,
  DropdownMenu,
  UnreadConversation,
} from '@design';
import { isJapaneseLocale } from '@helpers';
import { appPaths } from '@terminal/routes/app-paths';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import {
  HeaderCategoryItem,
  UnreadConversationType,
} from '../category-group/HeaderCategoryGroup';
import styles from './AlfredNavItem.module.scss';
import navStyles from './NavItem.module.scss';

const MAX_DISPLAYED_CONVERSATIONS = 5;
const ONE_LINE_HEIGHT = 48;
const TWO_LINE_HEIGHT = 65;
const THREE_LINE_HEIGHT = 85;

const calculateItemHeight = (question?: string): number => {
  if (!question) {
    return ONE_LINE_HEIGHT;
  }

  const length = question.length;

  if (length <= 45) {
    return ONE_LINE_HEIGHT;
  }

  if (length <= 89) {
    return TWO_LINE_HEIGHT;
  }

  return THREE_LINE_HEIGHT;
};

export type AlfredNavItemProps = {
  item: HeaderCategoryItem;
  isLoading?: boolean;
  isDefault?: boolean;
  isActive?: boolean;
  disabled?: boolean;
  isFinished?: boolean;
  unreadConversations?: UnreadConversationType[];
  onConversationClick?: (conversationId: string) => void;
};

export const AlfredNavItem = ({
  item,
  isLoading,
  isDefault,
  isActive = false,
  disabled,
  isFinished,
  unreadConversations,
  onConversationClick,
}: AlfredNavItemProps) => {
  const location = useLocation();
  const isAlfredPage = location.pathname === appPaths.alfred;
  const [isOpen, setIsOpen] = useState(
    !isAlfredPage && !!unreadConversations?.length
  );
  const isJapanese = isJapaneseLocale();

  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevUnreadCountRef = useRef<number>(unreadConversations?.length || 0);

  useEffect(() => {
    const currentCount = unreadConversations?.length || 0;
    const prevCount = prevUnreadCountRef.current;

    if (!isAlfredPage && currentCount > prevCount) {
      setIsOpen(true);
    }

    prevUnreadCountRef.current = currentCount;
  }, [unreadConversations, isAlfredPage]);

  const itemsToDisplay = Math.min(
    MAX_DISPLAYED_CONVERSATIONS,
    unreadConversations?.length || 0
  );

  const dropdownMaxHeight =
    unreadConversations
      ?.slice(0, itemsToDisplay)
      .reduce((totalHeight, conversation) => {
        const question = conversation.tasks?.at(-1)?.input;
        return totalHeight + calculateItemHeight(question);
      }, 0) || 0;

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    if (unreadConversations?.length) {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  const conversationDropdownItems =
    unreadConversations?.map(unreadConversation => ({
      key: `conversation-${unreadConversation.id}`,
      label: (
        <UnreadConversation
          status={unreadConversation.status}
          question={unreadConversation.tasks?.at(-1)?.input}
          conversationName={unreadConversation.name}
        />
      ),
      onClick: () => {
        onConversationClick?.(unreadConversation.id);
      },
    })) || [];

  const renderBadge = () => {
    return (
      <div
        className={clsx(styles.gradientWrapper, {
          [styles.isLoading]: isLoading,
          [styles.isDefault]: isDefault,
          [styles.isFinished]: isFinished,
        })}
      >
        <a
          className={clsx(navStyles.buttonItem, {
            [navStyles.isActive]: isActive,
            [navStyles.pillStyle]: !!item.pillStyle,
            [navStyles.isAlfred]: item.isAlfred,
            [navStyles.isJapanese]: isJapanese,
            [navStyles.isLoadingSize]: isLoading,
            [navStyles.disabled]: disabled,
          })}
          href={appPaths.alfred}
          data-testid={`header-navigation-item-${item.key}`}
          onClick={e => {
            e.preventDefault();

            if (disabled) {
              return;
            }

            item.onClick(item, e);
          }}
        >
          <div className={navStyles.itemLeft}>
            <span className={navStyles.iconWrapper}>{item.icon}</span>
            <span
              className={clsx(navStyles.mainLabel, {
                [navStyles.isAlfred]: item.isAlfred,
                [navStyles.isJapanese]: isJapanese,
              })}
            >
              {item.label}
            </span>
          </div>
        </a>
      </div>
    );
  };

  return (
    <div
      className={navStyles.itemWrapper}
      key={item.key}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {conversationDropdownItems.length ? (
        <DropdownMenu
          className={styles.styledDropdown}
          key={'alfred'}
          open={isOpen}
          align="end"
          onOpenChange={setIsOpen}
          modal={false}
          triggerNode={renderBadge()}
          contentProps={{
            style: {
              '--dropdown-max-height': `${dropdownMaxHeight}px`,
            } as React.CSSProperties,
          }}
        >
          <DropdownGroup items={conversationDropdownItems} />
        </DropdownMenu>
      ) : (
        renderBadge()
      )}
    </div>
  );
};
